import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, Users, DollarSign, Home, ArrowRight } from 'lucide-react'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { StatusBadge } from '@/components/admin/StatusBadge'
import { Spinner } from '@/components/ui/Spinner'
import { fetchDashboardStats } from '@/lib/api'
import { formatCurrency } from '@/lib/pricing'
import type { Lead } from '@/types'

interface Stats {
  total_leads: number
  new_leads: number
  avg_build_value: number
  top_model: string | null
  monthly_lead_value: number
  recent_leads: Partial<Lead>[]
}

function StatCard({ icon: Icon, label, value, sub, color }: {
  icon: typeof TrendingUp
  label: string
  value: string
  sub?: string
  color: string
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-card">
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-2xl font-bold text-slate-900 mb-0.5">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  )
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchDashboardStats()
      .then(setStats)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  return (
    <AdminLayout>
      <PageHeader
        title="Dashboard"
        subtitle="Sales overview for L&M Custom Builders"
      />

      {loading && (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users}      label="Total Leads"       value={String(stats.total_leads)}          color="bg-sky-50 text-sky-600" />
            <StatCard icon={TrendingUp} label="New Leads"         value={String(stats.new_leads)}            color="bg-brand-50 text-brand-600" sub="Awaiting review" />
            <StatCard icon={DollarSign} label="Avg Build Value"   value={formatCurrency(stats.avg_build_value)} color="bg-emerald-50 text-emerald-600" />
            <StatCard icon={Home}       label="Top Model"         value={stats.top_model ?? '—'}             color="bg-purple-50 text-purple-600" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-800">Recent Submissions</h2>
              <Link to="/admin/leads" className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1">
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            {stats.recent_leads.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">No leads yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {stats.recent_leads.map((lead) => (
                  <Link
                    key={lead.id}
                    to={`/admin/leads/${lead.id}`}
                    className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors"
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-900">{lead.customer_name}</div>
                      <div className="text-xs text-slate-400">{lead.selected_model_name_snapshot}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-800">
                        {formatCurrency(lead.estimated_total_snapshot ?? 0)}
                      </div>
                      {lead.status && <StatusBadge status={lead.status} />}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {stats.monthly_lead_value > 0 && (
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700">
              <span className="font-semibold">This month's lead pipeline:</span>{' '}
              {formatCurrency(stats.monthly_lead_value)} in estimated build value
            </div>
          )}
        </>
      )}
    </AdminLayout>
  )
}
